import {
  call,
  delay,
  fork,
  put,
  race,
  take,
  takeLatest
} from 'redux-saga/effects';
import { message } from 'antd';
import {
  TRIGGER_THRESHOLD_GENERATION,
  TRIGGER_THRESHOLD_GENERATION_SUCCESS,
  triggerThresholdGenerationSuccess,
  triggerThresholdGenerationFail,
  DISCARD_THRESHOLD_GENERATION,
  discardThresholdGenerationSuccess,
  discardThresholdGenerationFail,
  FETCH_THRESHOLD_RESULTS,
  fetchThresholdResultsSuccess,
  fetchThresholdResultsFail,
  FETCH_THRESHOLD_STATUS,
  fetchThresholdStatusSuccess,
  fetchThresholdStatusFail,
  FETCH_THRESHOLD_CONFIG,
  fetchThresholdConfigSuccess,
  THRESHOLD_GENERATION_RESET
} from '../../redux/actions/trm/thresholdGeneration';
import { showValidationErrors } from '../../redux/actions/trm/trmDashboard';

import * as api from '../../services/trm/trmServices';
const networkLayerObject = new api.NetworkLayer();

const POLL_INTERVAL = 30000;

export function* triggerThresholdGenerationWorker(payload) {
  try {
    // FIN-11922: mirror the scenario-launch gate (FIN-11800). Run the SAME
    // GET /scenarios/{id}/validate check before triggering threshold
    // generation and, on failure, surface the SAME consolidated
    // ScenarioValidationErrorModal (via SHOW_VALIDATION_ERRORS) instead of a
    // generic toast — consistent UX with Launch. The backend
    // POST /scenarios/{id}/thresholdGeneration enforces the same gate (HTTP
    // 422) as defense-in-depth for non-UI callers and validate/trigger races;
    // that path still falls back to message.error below.
    const scenarioId = payload.scenarioId || payload.scenarioID;
    const validateResponse = yield call(
      networkLayerObject.validateScenarioAPI,
      scenarioId
    );
    // FIN-11922 (fail-closed): a successful /validate returns { errors: [...] }.
    // getDataFromServer swallows HTTP errors into an Error object, so anything
    // without an `errors` ARRAY means validation could NOT be performed — do
    // NOT trigger generation with unknown validation status. Surface and stop.
    if (!validateResponse || !Array.isArray(validateResponse.errors)) {
      yield put(triggerThresholdGenerationFail('validation-unavailable'));
      message.error('Could not validate the scenario rules. Please try again.');
      return;
    }
    const errors = validateResponse.errors;
    if (errors.length > 0) {
      yield put(showValidationErrors({ scenarioID: scenarioId, errors }));
      yield put(triggerThresholdGenerationFail('validation'));
      return;
    }

    const response = yield call(
      networkLayerObject.triggerThresholdGenerationAPI,
      payload
    );
    yield put(triggerThresholdGenerationSuccess(response));
  } catch (error) {
    yield put(triggerThresholdGenerationFail(error?.message || error));
    message.error(error?.message || 'Threshold generation failed to start.');
  }
}

export function* watchTriggerThresholdGeneration() {
  yield takeLatest(TRIGGER_THRESHOLD_GENERATION, function* ({ payload }) {
    yield fork(triggerThresholdGenerationWorker, payload);
  });
}

function* pollThresholdStatus(scenarioId) {
  while (true) {
    try {
      const status = yield call(networkLayerObject.getThresholdStatusAPI, {
        scenarioId
      });

      yield put(fetchThresholdStatusSuccess(status));

      if (status.status === 'COMPLETED') {
        yield put({ type: FETCH_THRESHOLD_RESULTS, payload: { scenarioId } });
        message.success('Threshold generation completed successfully.');
        return;
      }

      if (status.status === 'FAILED' || status.status === 'none') {
        if (status.status === 'FAILED') {
          message.error('Threshold generation failed.');
        }
        return;
      }
    } catch (error) {
      yield put(fetchThresholdStatusFail(error?.message || error));
      return;
    }

    const { cancelled } = yield race({
      timeout: delay(POLL_INTERVAL),
      cancelled: take([
        THRESHOLD_GENERATION_RESET,
        DISCARD_THRESHOLD_GENERATION
      ])
    });

    if (cancelled) {
      return;
    }
  }
}

function* startPollingAfterTrigger() {
  while (true) {
    const { payload } = yield take(TRIGGER_THRESHOLD_GENERATION_SUCCESS);
    const scenarioId = payload.scenarioId || payload.scenarioInstanceId;
    if (scenarioId) {
      yield call(pollThresholdStatus, scenarioId);
    }
  }
}

export function* watchThresholdPolling() {
  yield fork(startPollingAfterTrigger);
}

function* fetchThresholdStatusWorker(payload) {
  try {
    const response = yield call(
      networkLayerObject.getThresholdStatusAPI,
      payload
    );
    yield put(fetchThresholdStatusSuccess(response));

    if (response.status === 'RUNNING') {
      yield call(pollThresholdStatus, payload.scenarioId);
    }
  } catch (error) {
    yield put(fetchThresholdStatusFail(error?.message || error));
  }
}

export function* watchFetchThresholdStatus() {
  yield takeLatest(FETCH_THRESHOLD_STATUS, function* ({ payload }) {
    yield fork(fetchThresholdStatusWorker, payload);
  });
}

function* discardThresholdGenerationWorker(payload) {
  try {
    const response = yield call(
      networkLayerObject.discardThresholdGenerationAPI,
      payload
    );
    yield put(discardThresholdGenerationSuccess(response));
  } catch (error) {
    yield put(discardThresholdGenerationFail(error?.message || error));
  }
}

export function* watchDiscardThresholdGeneration() {
  yield takeLatest(DISCARD_THRESHOLD_GENERATION, function* ({ payload }) {
    yield fork(discardThresholdGenerationWorker, payload);
  });
}

function* fetchThresholdResultsWorker(payload) {
  try {
    const response = yield call(
      networkLayerObject.getThresholdResultsAPI,
      payload
    );
    yield put(fetchThresholdResultsSuccess(response));
  } catch (error) {
    yield put(fetchThresholdResultsFail(error?.message || error));
  }
}

export function* watchFetchThresholdResults() {
  yield takeLatest(FETCH_THRESHOLD_RESULTS, function* ({ payload }) {
    yield fork(fetchThresholdResultsWorker, payload);
  });
}

function* fetchThresholdConfigWorker(payload) {
  try {
    const response = yield call(
      networkLayerObject.getThresholdConfigAPI,
      payload
    );
    yield put(fetchThresholdConfigSuccess(response));
  } catch (error) {
    // Config fetch failure is non-critical; state remains unchanged
  }
}

export function* watchFetchThresholdConfig() {
  yield takeLatest(FETCH_THRESHOLD_CONFIG, function* ({ payload }) {
    yield fork(fetchThresholdConfigWorker, payload);
  });
}
